/**
 * Нижняя навигация: плита, верхний край которой ведёт себя как поверхность жидкости.
 *
 * Назначение файла: собрать панель из геометрии (trough.ts), пружины (spring.ts)
 * и иконок (NavIcon.tsx). Выбранный раздел — шарик, под которым край плиты
 * проседает; впадина наклоняется по ходу движения и осаживается на месте.
 *
 * Что здесь важно по ТЗ:
 *   - ни одной текстовой строки: подписи приходят ключами из @hive/i18n (закон 11);
 *   - клавиатура: стрелки, Home и End, видимое кольцо фокуса (§23.5 доступность);
 *   - prefers-reduced-motion: шарик переставляется мгновенно, без кадров;
 *   - кадры просятся только когда что-то двигается, и цикл сам останавливается,
 *     иначе панель будет греть телефон на каждом экране.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { useReducedMotion } from "../auth/useReducedMotion.js";
import { NavIcon, type NavIconName } from "./NavIcon.jsx";
import { DRAG, isAtRest, normalizeVelocity, SETTLE, stepSpring } from "./spring.js";
import { troughPath } from "./trough.js";

export interface LiquidNavItem {
  readonly id: string;
  readonly icon: NavIconName;
  readonly label: string;
}

export interface LiquidNavProps {
  readonly items: readonly LiquidNavItem[];
  readonly activeId: string;
  readonly ariaLabel: string;
  readonly onChange: (id: string) => void;
}

/** Высота плиты. Шарик считается от неё, поэтому в одном месте. */
const BAR_HEIGHT = 64;

/** Скругление углов плиты: внешнее плечо впадины обязано его обойти. */
const BAR_CORNER = 22;

/** Доля высоты плиты, которую занимает диаметр шарика. */
const BEAD_SHARE = 0.78;

/** Ниже этой ширины панели впадину не строим: считать нечего. */
const MIN_BAR_WIDTH = 120;

/**
 * Запас над краем плиты. Шарик стоит центром ровно на краю, значит его верхняя
 * половина выходит за плиту, и без запаса она обрезалась бы рамкой svg.
 */
const PLATE_PAD = 44;

interface Metrics {
  readonly width: number;
  readonly centers: readonly number[];
}

export function LiquidNav({
  items,
  activeId,
  ariaLabel,
  onChange,
}: LiquidNavProps): React.JSX.Element {
  const reducedMotion = useReducedMotion();
  const barRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const [metrics, setMetrics] = useState<Metrics>({ width: 0, centers: [] });
  const [beadX, setBeadX] = useState(0);
  const [velocity, setVelocity] = useState(0);

  const springRef = useRef({ value: 0, velocity: 0 });
  const frameRef = useRef(0);
  const lastTimeRef = useRef(0);
  const targetRef = useRef(0);
  const draggingRef = useRef(false);

  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.id === activeId),
  );

  /** Померить панель по факту: центры разделов зависят от длины подписей. */
  const measure = useCallback(() => {
    const bar = barRef.current;
    if (!bar) return;
    const barBox = bar.getBoundingClientRect();
    const centers = items.map((_, index) => {
      const tab = tabsRef.current[index];
      if (!tab) return barBox.width / 2;
      const box = tab.getBoundingClientRect();
      return box.left - barBox.left + box.width / 2;
    });
    setMetrics({ width: barBox.width, centers });
  }, [items]);

  useLayoutEffect(() => {
    measure();
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [measure]);

  const stopLoop = useCallback(() => {
    if (frameRef.current !== 0) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    }
  }, []);

  const runLoop = useCallback(() => {
    if (frameRef.current !== 0) return;
    lastTimeRef.current = 0;

    const tick = (now: number) => {
      const previous = lastTimeRef.current;
      lastTimeRef.current = now;
      const dt = previous === 0 ? 1 / 60 : (now - previous) / 1000;

      const tuning = draggingRef.current ? DRAG : SETTLE;
      const next = stepSpring(springRef.current, targetRef.current, dt, tuning);
      springRef.current = next;
      setBeadX(next.value);
      setVelocity(normalizeVelocity(next.velocity, metrics.width));

      if (isAtRest(next, targetRef.current) && !draggingRef.current) {
        springRef.current = { value: targetRef.current, velocity: 0 };
        setBeadX(targetRef.current);
        setVelocity(0);
        frameRef.current = 0;
        return;
      }
      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
  }, [metrics.width]);

  // Шарик едет к выбранному разделу. Мгновенно — при первом показе и при
  // отключённой анимации: иначе панель на старте проезжает через все разделы.
  useEffect(() => {
    const target = metrics.centers[activeIndex];
    if (target === undefined) return;
    targetRef.current = target;

    const isFirstPlacement = springRef.current.value === 0;
    if (reducedMotion || isFirstPlacement) {
      springRef.current = { value: target, velocity: 0 };
      setBeadX(target);
      setVelocity(0);
      stopLoop();
      return;
    }
    runLoop();
  }, [activeIndex, metrics.centers, reducedMotion, runLoop, stopLoop]);

  useEffect(() => stopLoop, [stopLoop]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      const last = items.length - 1;
      let next: number | null = null;
      if (event.key === "ArrowRight") next = Math.min(last, activeIndex + 1);
      if (event.key === "ArrowLeft") next = Math.max(0, activeIndex - 1);
      if (event.key === "Home") next = 0;
      if (event.key === "End") next = last;
      if (next === null) return;

      event.preventDefault();
      const item = items[next];
      if (!item) return;
      tabsRef.current[next]?.focus();
      onChange(item.id);
    },
    [activeIndex, items, onChange],
  );

  const beadRadius = (BAR_HEIGHT * BEAD_SHARE) / 2;
  const hasRoom = metrics.width >= MIN_BAR_WIDTH;
  const geometry = troughPath({
    width: metrics.width,
    height: BAR_HEIGHT,
    corner: BAR_CORNER,
    beadX: hasRoom ? beadX : metrics.width / 2,
    beadRadius: hasRoom ? beadRadius : 0,
    velocity,
  });

  return (
    <nav className="liquid-nav" aria-label={ariaLabel}>
      <div className="liquid-nav__bar" ref={barRef}>
        {metrics.width > 0 && (
          <svg
            className="liquid-nav__plate"
            viewBox={`0 ${-PLATE_PAD} ${metrics.width} ${BAR_HEIGHT + PLATE_PAD}`}
            width={metrics.width}
            height={BAR_HEIGHT + PLATE_PAD}
            style={{ insetBlockStart: `${-PLATE_PAD}px` }}
            aria-hidden="true"
            focusable="false"
          >
            <path className="liquid-nav__plate-fill" d={geometry.path} />
            {hasRoom && (
              <ellipse
                className="liquid-nav__bead"
                cx={beadX}
                cy={0}
                rx={geometry.beadRx}
                ry={geometry.beadRy}
              />
            )}
          </svg>
        )}

        <ul className="liquid-nav__list">
          {items.map((item, index) => {
            const isActive = index === activeIndex;
            // Близость шарика, 0…1: иконки поднимаются ему навстречу, пока он едет мимо.
            const center = metrics.centers[index] ?? 0;
            const span = metrics.width / Math.max(1, items.length);
            const nearness = hasRoom
              ? Math.max(0, 1 - Math.abs(beadX - center) / Math.max(1, span))
              : 0;

            return (
              <li className="liquid-nav__item" key={item.id}>
                <button
                  type="button"
                  ref={(node) => {
                    tabsRef.current[index] = node;
                  }}
                  className={
                    isActive ? "liquid-nav__tab liquid-nav__tab--active" : "liquid-nav__tab"
                  }
                  style={{ "--t": nearness } as React.CSSProperties}
                  aria-current={isActive ? "page" : undefined}
                  tabIndex={isActive ? 0 : -1}
                  onKeyDown={handleKeyDown}
                  onClick={() => onChange(item.id)}
                >
                  <NavIcon name={item.icon} />
                  <span className="liquid-nav__label">{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
